import {
  Box,
  Button,
  FormControl,
  Grid2,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { CompanyConfigsInfo, menuItem } from "IF/interfaceType";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import axios from "axios";
import { MessageModal } from "./MessageModal";
import { UserRole, useSessionStorage } from "IF/userSession";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { DataContext } from "Contexts/DataProvider";
import { extractFileName } from "Utils/file";
import { createTitle } from "Utils/format";
import ConfirmationModal from "./ConfirmationModal";

export const companyConfigsInitData: CompanyConfigsInfo = {
  value: "",
  category: "",
  key: "",
  createUser: "",
  updateUser: "",
};

export const SettingVarious = () => {
  // ユーザ情報
  const { getUserData } = useSessionStorage();
  const userData = getUserData();
  const { id: userId, companyCode, userRole } = userData;

  const initialMenuItems: string[] = [
    "ホーム",
    "アラート履歴",
    "レポート",
    "設定",
    "作業者管理",
    "エリア管理",
    "エリア管理履歴", // 2026.03.04 add by Y.Sakamoto
    "配信管理",
    "ログイン管理",
    "各種設定",
  ];

  interface MenuItem {
    id: string;
    label: string;
    children?: MenuItem[];
  }

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { menuItems, setMenuItems, setChoiceMenu, toggleMenu } = sidebarContext;

  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  // メニュー項目名一覧
  const [data, setData] = useState<CompanyConfigsInfo[]>([]);
  // 保存期間情報
  const [retentionPeriod, setRetentionPeriod] = useState<CompanyConfigsInfo[]>(
    []
  );
  // 保存期間（データ）
  const [retentionPeriodData, setRetentionPeriodData] =
    useState<CompanyConfigsInfo>(companyConfigsInitData);
  // 保存期間（ログ）
  const [retentionPeriodLog, setRetentionPeriodLog] =
    useState<CompanyConfigsInfo>(companyConfigsInitData);

  // 端末通知条件
  const [notificationDelay, setNotificationDelay] = useState<
    CompanyConfigsInfo[]
  >([]);
  const [notificationDelayData, setNotificationDelayData] =
    useState<CompanyConfigsInfo>(companyConfigsInitData);

  // 不安全警告表示条件
  const [alertDisplayWaitData, setAlertDisplayWaitData] =
    useState<CompanyConfigsInfo>(companyConfigsInitData);

  // Line情報
  const [lineInfo, setLineInfo] = useState<CompanyConfigsInfo[]>([]);

  // direct情報
  const [directInfo, setDirectInfo] = useState<CompanyConfigsInfo[]>([]);

  // レンタル会社メールアドレス情報
  const [rentalCompanyInfo, setRentalCompanyInfo] =
    useState<CompanyConfigsInfo>(companyConfigsInitData);

  // Line配信有無
  const [lineSendStatus, setLineSendStatus] = useState<CompanyConfigsInfo>(
    companyConfigsInitData
  );
  // Lineアクセストークン
  const [lineAccessToken, setLineAccessToken] = useState<CompanyConfigsInfo>(
    companyConfigsInitData
  );
  // direct配信有無
  const [directSendStatus, setDirectSendStatus] = useState<CompanyConfigsInfo>(
    companyConfigsInitData
  );
  // directアクセストークン
  const [directAccessToken, setDirectAccessToken] =
    useState<CompanyConfigsInfo>(companyConfigsInitData);
  // directトークID
  const [directTalkId, setDirectTalkId] = useState<CompanyConfigsInfo>(
    companyConfigsInitData
  );
  // レンタル会社メールアドレス
  const [rentalCompanyEmail, setRentalCompanyEmail] =
    useState<CompanyConfigsInfo>(companyConfigsInitData);

  // 編集フォーム
  const [editFormData, setEditFormData] = useState<CompanyConfigsInfo>(
    companyConfigsInitData
  );
  // 編集対象の行のindex
  const [editIndex, setEditIndex] = useState<number | null>(null);
  // 初期表示時メニュー名
  const [initEditFormData, setInitEditFormData] = useState<
    CompanyConfigsInfo[]
  >([]);

  // モーダル
  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
  };

  // 確認モーダル
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<React.ReactNode>("");
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const handleConfirmModalClose = () => setConfirmModalOpen(false);

  const handleConfirmModalOpen = (
    message: React.ReactNode,
    confirmAction: () => void
  ) => {
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  // 隠しファイル入力への参照を作成
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getAlertNoticeConfig = (list: CompanyConfigsInfo[]) => {
    return (
      list.find((x) => x.key === "alert_notice_delay") ??
      list[0] ??
      companyConfigsInitData
    );
  };

  const getAlertDisplayWaitConfig = (list: CompanyConfigsInfo[]) => {
    return (
      list.find((x) => x.key === "alert_display_wait") ??
      list[1] ??
      companyConfigsInitData
    );
  };

  // 初期データ取得処理
  const initGetData = useCallback(async (schema: string) => {
    getData(schema);
    getNotificationDelay(schema);
    getRetentionPeriod(schema);
    getLineData(schema);
    getDirectData(schema);
    getRentalCompanyEmail(schema);
  }, []);

  // タイトル設定
  useEffect(() => {
    if (menuItems.length > 2) {
      const index = UserRole.YKC !== userRole ? 3 : 4;
      document.title = createTitle(menuItems[index]?.subItems?.[5]?.text ?? "");
      setChoiceMenu(menuItems[index]?.subItems?.[5]?.text ?? "");
      toggleMenu("設定");
    }
  }, [userRole, menuItems, setChoiceMenu, toggleMenu]);

  // 初期表示時処理
  useEffect(() => {
    // 初期表示データ取得
    initGetData(companyCode);
  }, [companyCode, initGetData]);

  // メニュー項目名データ取得処理
  const getData = async (schema: string) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/VariousSetting/GetCompanyConfigs`,
        {
          params: { schema: schema, category: "menu" },
        }
      );
      if (response.data !== "") {
        setData(response.data);
        setInitEditFormData(response.data);
      } else {
        setModalOpen(true);
        setMessage("メニュー情報がありません。");
      }
    } catch (error) {
      console.error("メニュー情報の取得に失敗しました:", error);
    }
  };

  // 端末通知条件データ取得処理
  const getNotificationDelay = async (schema: string) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/VariousSetting/GetCompanyConfigs`,
        {
          params: { schema: schema, category: "alert" },
        }
      );
      if (response.data !== "") {
        const alertList = response.data as CompanyConfigsInfo[];
        setNotificationDelay(alertList);
        setNotificationDelayData(getAlertNoticeConfig(alertList));
        setAlertDisplayWaitData(getAlertDisplayWaitConfig(alertList));
      }
    } catch (error) {
      console.error("端末通知条件情報の取得に失敗しました:", error);
    }
  };

  // 保存期間データ取得処理
  const getRetentionPeriod = async (schema: string) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/VariousSetting/GetCompanyConfigs`,
        {
          params: { schema: schema, category: "retention_period" },
        }
      );
      if (response.data !== "") {
        setRetentionPeriod(response.data);
        setRetentionPeriodData(response.data[0]);
        setRetentionPeriodLog(response.data[1]);
      }
    } catch (error) {
      console.error("保存期間情報の取得に失敗しました:", error);
    }
  };

  // line配信情報データ取得処理
  const getLineData = async (schema: string) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/VariousSetting/GetCompanyConfigs`,
        {
          params: { schema: schema, category: "line" },
        }
      );
      if (response.data !== "") {
        setLineInfo(response.data);
        setLineSendStatus(response.data[0]);
        setLineAccessToken(response.data[1]);
      } else {
        setModalOpen(true);
        setMessage("LINE配信情報がありません。");
      }
    } catch (error) {
      setModalOpen(true);
      setMessage("LINE配信情報の取得に失敗しました");
      console.error("LINE配信情報の取得に失敗しました:", error);
    }
  };

  // direct配信情報データ取得処理
  const getDirectData = async (schema: string) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/VariousSetting/GetCompanyConfigs`,
        {
          params: { schema: schema, category: "direct" },
        }
      );
      if (response.data !== "") {
        setDirectInfo(response.data);
        setDirectSendStatus(response.data[0]);
        setDirectAccessToken(response.data[1]);
        setDirectTalkId(response.data[2]);
      } else {
        setModalOpen(true);
        setMessage("direct配信情報がありません。");
      }
    } catch (error) {
      setModalOpen(true);
      setMessage("direct配信情報の取得に失敗しました");
      console.error("direct配信情報の取得に失敗しました:", error);
    }
  };

  // レンタル会社e-mail取得処理
  const getRentalCompanyEmail = async (schema: string) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/VariousSetting/GetCompanyConfigs`,
        {
          params: { schema: schema, category: "local_reset" },
        }
      );
      setRentalCompanyEmail(response.data[0]);
      setRentalCompanyInfo(response.data[0]);
    } catch (error) {
      console.error(
        "レンタル会社CSV送信メールアドレス情報の取得に失敗しました:",
        error
      );
    }
  };

  // 編集ボタン押下時の処理
  const handleEdit = (index: number) => {
    setEditIndex(index);
    setEditFormData(data[index]);
  };

  /**
   * 編集中のデータ変更
   * @param e イベント
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editFormData) return;
    setEditFormData({
      ...editFormData,
      value: e.target.value,
      updateUser: userId,
    });
  };

  const handleSelectChangeAlert = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    setNotificationDelayData({
      ...notificationDelayData,
      value: selectedValue,
      updateUser: userId,
    });
  };

  const handleSelectChangeAlertDisplayWait = (
    event: SelectChangeEvent<string>
  ) => {
    const selectedValue = event.target.value as string;
    setAlertDisplayWaitData({
      ...alertDisplayWaitData,
      value: selectedValue,
      updateUser: userId,
    });
  };

  const handleSelectChangeData = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    setRetentionPeriodData({
      ...retentionPeriodData,
      value: selectedValue,
      updateUser: userId,
    });
  };

  const handleSelectChangeLog = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    setRetentionPeriodLog({
      ...retentionPeriodLog,
      value: selectedValue,
      updateUser: userId,
    });
  };

  const handleSelectChangeLineStatus = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    setLineSendStatus({
      ...lineSendStatus,
      value: selectedValue,
      updateUser: userId,
    });
  };

  const handleSelectChangeDataDirectStatus = (
    event: SelectChangeEvent<string>
  ) => {
    const selectedValue = event.target.value as string;
    setDirectSendStatus({
      ...directSendStatus,
      value: selectedValue,
      updateUser: userId,
    });
  };

  // 編集キャンセル
  const handleCancel = () => {
    setEditIndex(null);
  };

  // メニュー項目名更新時処理
  const handleMenuUpdate = async () => {
    //変更有無チェック
    if (
      editIndex !== null &&
      editFormData.value === initEditFormData[editIndex].value
    ) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    await handleUpdate(editFormData, "メニュー項目名");
  };

  // 端末通知条件更新時処理
  const handleUpdateAlertNoticeDelay = async () => {
    const currentNotice = getAlertNoticeConfig(notificationDelay);

    //変更有無チェック
    if (notificationDelayData.value === currentNotice.value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(notificationDelayData, "端末通知条件");
  };

  // 不安全警告表示条件更新時処理
  const handleUpdateAlertDisplayWait = async () => {
    const currentDisplayWait = getAlertDisplayWaitConfig(notificationDelay);

    //変更有無チェック
    if (alertDisplayWaitData.value === currentDisplayWait.value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(alertDisplayWaitData, "不安全警告表示条件");
  };

  // データ保存期間更新時処理
  const handleRetentionPeriodData = async () => {
    //変更有無チェック
    if (retentionPeriodData.value === retentionPeriod[0].value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(retentionPeriodData, "データ保存期間");
  };

  // ログ保存期間更新時処理
  const handleRetentionPeriodLog = async () => {
    //変更有無チェック
    if (retentionPeriodLog.value === retentionPeriod[1].value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(retentionPeriodLog, "ログ保存期間");
  };

  // Line配信有無更新時処理
  const handleUpdateLineStatus = async () => {
    //変更有無チェック
    if (lineSendStatus.value === lineInfo[0].value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(lineSendStatus, "Line配信有無");
  };

  // Lineアクセストークン更新時処理
  const handleUpdateLineAccessToken = async () => {
    //変更有無チェック
    if (lineAccessToken.value === lineInfo[1].value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(lineAccessToken, "LINEアクセストークン");
  };

  // direct配信有無更新時処理
  const handleUpdateDirectStatus = async () => {
    //変更有無チェック
    if (directSendStatus.value === directInfo[0].value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(directSendStatus, "direct配信有無");
  };

  // directアクセストークン更新時処理
  const handleUpdateDirectAccessToken = async () => {
    //変更有無チェック
    if (directAccessToken.value === directInfo[1].value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(directAccessToken, "directアクセストークン");
  };

  // directトークID更新時処理
  const handleUpdateDirectTalkId = async () => {
    //変更有無チェック
    if (directTalkId.value === directInfo[2].value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(directTalkId, "directトークID");
  };

  // レンタル会社CSV送信メールアドレス更新時処理
  const handleUpdateRentalEmail = async () => {
    //変更有無チェック
    if (rentalCompanyEmail.value === rentalCompanyInfo.value) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    handleUpdate(rentalCompanyEmail, "レンタル会社CSV送信メールアドレス");
  };

  // 更新時処理
  const handleUpdate = async (
    updateData: CompanyConfigsInfo,
    target: string
  ) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/VariousSetting/PutCompanyConfigs`,
        {
          schema: companyCode,
          CompanyConfigsData: updateData,
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage(`${target}を更新しました。`);

        if (
          target === "メニュー項目名" &&
          updateData.value !== undefined &&
          editIndex !== null &&
          initEditFormData[editIndex] !== undefined
        ) {
          const updated = updateMenuItems(
            menuItems,
            updateData.value,
            initEditFormData[editIndex].value
          );
          setMenuItems(updated);
        }

        handleCancel();
        initGetData(companyCode);
      }
    } catch (error: any) {
      if (error.response) {
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
        setModalOpen(true);
        setMessage(error.response.data);
      } else {
        setModalOpen(true);
        setMessage(`${target}の更新に失敗しました。`);
        console.error(`${target}の更新に失敗しました。`, error);
      }
    }
  };

  function updateMenuItems(
    menuItems: menuItem[],
    newValue: string,
    oldValue?: string
  ): menuItem[] {
    return menuItems.map((item) => {
      // 1. メインの text が一致した場合
      let updatedItem: menuItem = { ...item };
      if (item.text === oldValue) {
        updatedItem.text = newValue;
      }

      // 2. subItems があれば、その中の text もチェック
      if (item.subItems && item.subItems.length > 0) {
        const updatedSubItems = item.subItems.map((subItem) => {
          if (subItem.text === oldValue) {
            return { ...subItem, text: newValue };
          }
          return subItem;
        });

        updatedItem = { ...updatedItem, subItems: updatedSubItems };
      }

      return updatedItem;
    });
  }

  // 現場リセット処理
  const handleLocalReset = async () => {
    try {
      // API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/VariousSetting/LocalReset`,
        {
          companyCode: companyCode,
          companyName: userData.companyName,
          id: userId,
          userRole: userRole,
        }
      );

      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("現場リセットが完了しました。");
        // データ取得
        initGetData(companyCode);
      }
    } catch (error) {
      setModalOpen(true);
      setMessage("現場リセットに失敗しました。");
      console.error("現場リセットに失敗しました:", error);
    }
  };

  // 履歴データ削除処理
  const handleHistoriesDelete = async () => {
    try {
      // 1. API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/VariousSetting/HistoriesDelete`,
        {
          companyCode: companyCode,
          companyName: userData.companyName,
          id: userId,
          userRole: userRole,
        },
        {
          responseType: "blob",
        }
      );

      const contentDisposition = response.headers["content-disposition"];
      // ファイル名取得
      const fileName = extractFileName(contentDisposition);

      // 2. Blobを生成
      const blob = new Blob([response.data], { type: "application/zip" });

      // 3. Blob URLを作成
      const url = window.URL.createObjectURL(blob);

      // 4. ダウンロード用のリンクを作り、クリックしてダウンロードを開始
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();

      // 5. 使用済みBlob URLを解放
      window.URL.revokeObjectURL(url);
      // データ取得
      initGetData(companyCode);
    } catch (error) {
      setModalOpen(true);
      setMessage("履歴データの削除に失敗しました。");
      console.error("履歴データの削除に失敗しました:", error);
    }
  };

  const uploadZipFile = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/VariousSetting/ZipImport`,
        {
          companyCode: companyCode,
          companyName: userData.companyName,
          id: userId,
          userRole: userRole,
        }
      );

      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("データの取り込みが完了しました。");
        // データ取得
        initGetData(companyCode);
      }
    } catch (error: any) {
      if (error.response) {
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
        setModalOpen(true);
        setMessage(error.response.data);
      } else {
        setModalOpen(true);
        setMessage("データの取り込みに失敗しました。");
        console.error("データの取り込みに失敗しました。", error);
      }
    }
  };

  // モーダルのOKボタン押下時の処理
  const handleConfirm = () => {
    setConfirmModalOpen(false);
    uploadZipFile();
    // 隠しファイル入力をクリックしてファイル選択ダイアログを表示
    // if (fileInputRef.current) {
    //   fileInputRef.current.click();
    // }
  };

  return (
    <>
      <MessageModal
        open={isModalOpen}
        message={message}
        handleClose={closeModal}
      />
      <ConfirmationModal
        open={confirmModalOpen}
        message={confirmMessage}
        handleClose={handleConfirmModalClose}
        onConfirm={onConfirm!}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Grid2 container>
          <Typography fontWeight="fontWeightBold">アラート設定</Typography>
          <TableContainer component={Paper}>
            <Table sx={{ width: "100%", tableLayout: "fixed" }}>
              <TableBody>
                <TableRow
                  sx={{
                    "& td": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <TableCell
                    size="small"
                    component="th"
                    scope="row"
                    align="center"
                    sx={{
                      fontWeight: "fontWeightRegular",
                      backgroundColor: theme.palette.custom.tableHeader,
                      color: "white",
                      width: "40%",
                    }}
                  >
                    不安全警告表示条件
                  </TableCell>
                  <TableCell
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.custom.row,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        backgroundColor: theme.palette.custom.row,
                      }}
                    >
                      <Box
                        sx={{
                          display: {
                            xs: "none",
                            sm: "block",
                            md: "block",
                            lg: "block",
                          },
                        }}
                      >
                        現場で
                      </Box>
                      <FormControl sx={{ minWidth: 80, pr: 0 }}>
                        <Select
                          value={alertDisplayWaitData?.value ?? ""}
                          onChange={handleSelectChangeAlertDisplayWait}
                          displayEmpty
                        >
                          <MenuItem value="0">0</MenuItem>
                          <MenuItem value="5">5</MenuItem>
                          <MenuItem value="10">10</MenuItem>
                          <MenuItem value="15">15</MenuItem>
                          <MenuItem value="20">20</MenuItem>
                          <MenuItem value="25">25</MenuItem>
                          <MenuItem value="30">30</MenuItem>
                          <MenuItem value="60">60</MenuItem>
                          <MenuItem value="90">90</MenuItem>
                          <MenuItem value="120">120</MenuItem>
                        </Select>
                      </FormControl>
                      <Box
                        sx={{
                          px: 0,
                          mx: 0,
                        }}
                      >
                        {isLgDown
                          ? "秒後"
                          : "秒警報が継続した場合、ホーム画面に不安全警告表示する"}
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "20%",
                      backgroundColor: theme.palette.custom.row,
                      px: 0,
                      mx: 0,
                    }}
                  >
                    <Button
                      onClick={handleUpdateAlertDisplayWait}
                      sx={{
                        width: {
                          sm: "10vh",
                          md: "13vh",
                          lg: "20vh",
                        },
                      }}
                      variant="contained"
                      color="primary"
                      style={{ backgroundColor: "#000080" }}
                    >
                      更新
                    </Button>
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{
                    "& td": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <TableCell
                    size="small"
                    component="th"
                    scope="row"
                    align="center"
                    sx={{
                      fontWeight: "fontWeightRegular",
                      backgroundColor: theme.palette.custom.tableHeader,
                      color: "white",
                      width: "40%",
                    }}
                  >
                    {isSmDown ? "アラート通知" : "端末通知条件"}
                  </TableCell>
                  <TableCell
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.custom.row,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        backgroundColor: theme.palette.custom.row,
                      }}
                    >
                      <Box
                        sx={{
                          display: {
                            xs: "none",
                            sm: "block",
                            md: "block",
                            lg: "block",
                          },
                        }}
                      >
                        不安全警告表示のまま
                      </Box>
                      <FormControl sx={{ minWidth: 80, pr: 0 }}>
                        <Select
                          value={notificationDelayData?.value ?? ""}
                          onChange={handleSelectChangeAlert}
                          displayEmpty
                        >
                          <MenuItem value="0">0</MenuItem>
                          <MenuItem value="30">30</MenuItem>
                          <MenuItem value="60">60</MenuItem>
                          <MenuItem value="120">120</MenuItem>
                        </Select>
                      </FormControl>
                      <Box
                        sx={{
                          px: 0,
                          mx: 0,
                        }}
                      >
                        {isLgDown ? "秒後" : "経過した場合、端末に通知する"}
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "20%",
                      backgroundColor: theme.palette.custom.row,
                      px: 0,
                      mx: 0,
                    }}
                  >
                    <Button
                      onClick={handleUpdateAlertNoticeDelay}
                      sx={{
                        width: {
                          sm: "10vh",
                          md: "13vh",
                          lg: "20vh",
                        },
                      }}
                      variant="contained"
                      color="primary"
                      style={{ backgroundColor: "#000080" }}
                    >
                      更新
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid2>

        <Grid2 container>
          <Typography fontWeight="fontWeightBold" sx={{ mb: "0px", pb: "0px" }}>
            メニュー項目名変更
          </Typography>
          <TableContainer component={Paper}>
            <Table
              sx={{
                "& th, & td": {
                  textAlign: "center",
                  verticalAlign: "middle",
                },
                "& th": {
                  color: "#fff",
                },
              }}
            >
              <TableBody>
                {data.map((item, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell
                        size="small"
                        component="th"
                        scope="row"
                        align="center"
                        sx={{
                          fontWeight: "fontWeightRegular",
                          backgroundColor: theme.palette.custom.tableHeader,
                          color: "white",
                          width: "40%",
                        }}
                      >
                        {initialMenuItems[index]}
                      </TableCell>
                      {editIndex === index ? (
                        <TableCell
                          size="small"
                          sx={{ backgroundColor: theme.palette.custom.row }}
                        >
                          <FormControl fullWidth>
                            <TextField
                              size="small"
                              variant="outlined"
                              value={editFormData?.value}
                              onChange={handleChange}
                            />
                          </FormControl>
                        </TableCell>
                      ) : (
                        <TableCell
                          size="small"
                          sx={{ backgroundColor: theme.palette.custom.row }}
                        >
                          {item.value}
                        </TableCell>
                      )}
                      <TableCell
                        size="small"
                        sx={{
                          padding: "2px 4px",
                          verticalAlign: "middle",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        {editIndex === index ? (
                          <>
                            <Tooltip title={"保存"} placement="top" arrow>
                              <IconButton onClick={handleMenuUpdate}>
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={"キャンセル"} placement="top" arrow>
                              <IconButton onClick={handleCancel}>
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title={"編集"} placement="top" arrow>
                              <IconButton onClick={() => handleEdit(index)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid2>

        {(UserRole.YKC === userRole || UserRole.RENTAL === userRole) && (
          <>
            <Grid2 container>
              <Typography
                fontWeight="fontWeightBold"
                sx={{ mb: "0px", pb: "0px" }}
              >
                LINE配信
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="center"
                        sx={{
                          fontWeight: "fontWeightRegular",
                          backgroundColor: theme.palette.custom.tableHeader,
                          color: "white",
                          width: "40%",
                        }}
                      >
                        配信
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "40%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <FormControl fullWidth>
                          <Select
                            value={lineSendStatus?.value ?? ""}
                            onChange={handleSelectChangeLineStatus}
                          >
                            <MenuItem value="0">無し</MenuItem>
                            <MenuItem value="1">有り</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "20%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <Button
                          onClick={handleUpdateLineStatus}
                          sx={{
                            width: {
                              sm: "10vh",
                              md: "13vh",
                              lg: "20vh",
                            },
                          }}
                          variant="contained"
                          color="primary"
                          style={{ backgroundColor: "#000080" }}
                        >
                          更新
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="center"
                        sx={{
                          fontWeight: "fontWeightRegular",
                          backgroundColor: theme.palette.custom.tableHeader,
                          color: "white",
                          width: "40%",
                        }}
                      >
                        アクセストークン
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "40%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <FormControl fullWidth>
                          <TextField
                            variant="outlined"
                            value={lineAccessToken.value ?? ""}
                            onChange={(e) =>
                              setLineAccessToken({
                                ...lineAccessToken,
                                value: e.target.value,
                                updateUser: userId,
                              })
                            }
                          />
                        </FormControl>
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "20%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <Button
                          onClick={handleUpdateLineAccessToken}
                          sx={{
                            width: {
                              sm: "10vh",
                              md: "13vh",
                              lg: "20vh",
                            },
                          }}
                          variant="contained"
                          color="primary"
                          style={{ backgroundColor: "#000080" }}
                        >
                          更新
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid2>

            <Grid2 container>
              <Typography
                fontWeight="fontWeightBold"
                sx={{ mb: "0px", pb: "0px" }}
              >
                direct配信
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="center"
                        sx={{
                          fontWeight: "fontWeightRegular",
                          backgroundColor: theme.palette.custom.tableHeader,
                          color: "white",
                          width: "40%",
                        }}
                      >
                        配信
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "40%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <FormControl fullWidth>
                          <Select
                            value={directSendStatus?.value ?? ""}
                            onChange={handleSelectChangeDataDirectStatus}
                          >
                            <MenuItem value="0">無し</MenuItem>
                            <MenuItem value="1">有り</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "20%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <Button
                          onClick={handleUpdateDirectStatus}
                          sx={{
                            width: {
                              sm: "10vh",
                              md: "13vh",
                              lg: "20vh",
                            },
                          }}
                          variant="contained"
                          color="primary"
                          style={{ backgroundColor: "#000080" }}
                        >
                          更新
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="center"
                        sx={{
                          fontWeight: "fontWeightRegular",
                          backgroundColor: theme.palette.custom.tableHeader,
                          color: "white",
                          width: "40%",
                        }}
                      >
                        アクセストークン
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "40%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <FormControl fullWidth>
                          <TextField
                            variant="outlined"
                            value={directAccessToken.value ?? ""}
                            onChange={(e) =>
                              setDirectAccessToken({
                                ...directAccessToken,
                                value: e.target.value,
                                updateUser: userId,
                              })
                            }
                          />
                        </FormControl>
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "20%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <Button
                          onClick={handleUpdateDirectAccessToken}
                          sx={{
                            width: {
                              sm: "10vh",
                              md: "13vh",
                              lg: "20vh",
                            },
                          }}
                          variant="contained"
                          color="primary"
                          style={{ backgroundColor: "#000080" }}
                        >
                          更新
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        align="center"
                        sx={{
                          fontWeight: "fontWeightRegular",
                          backgroundColor: theme.palette.custom.tableHeader,
                          color: "white",
                          width: "40%",
                        }}
                      >
                        トークID
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "40%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <FormControl fullWidth>
                          <TextField
                            variant="outlined"
                            value={directTalkId.value ?? ""}
                            onChange={(e) =>
                              setDirectTalkId({
                                ...directTalkId,
                                value: e.target.value,
                                updateUser: userId,
                              })
                            }
                          />
                        </FormControl>
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          width: "20%",
                          backgroundColor: theme.palette.custom.row,
                        }}
                      >
                        <Button
                          onClick={handleUpdateDirectTalkId}
                          sx={{
                            width: {
                              sm: "10vh",
                              md: "13vh",
                              lg: "20vh",
                            },
                          }}
                          variant="contained"
                          color="primary"
                          style={{ backgroundColor: "#000080" }}
                        >
                          更新
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid2>

            <Grid2 container>
              <Box sx={{ mt: 1, ml: "auto" }}>
                <Button
                  onClick={() =>
                    handleConfirmModalOpen(
                      <Box>
                        Zipファイル取り込みを実行します。よろしいですか？
                        <br />
                        <span style={{ color: "red" }}>
                          ※現在のデータを初期化し、企業コードが一致する最新のバックアップデータを取り込みます。
                        </span>
                      </Box>,
                      () => handleConfirm()
                    )
                  }
                  sx={{ width: "20vh", ml: 1 }}
                  variant="contained"
                  color="primary"
                >
                  Zip取り込み
                </Button>
                <Button
                  onClick={() =>
                    handleConfirmModalOpen(
                      <Box>
                        現場リセットを実行します。よろしいですか？
                        <br />
                        <span style={{ color: "red" }}>
                          ※現在のデータをバックアップ後、初期化します。
                        </span>
                      </Box>,
                      () => handleLocalReset()
                    )
                  }
                  sx={{ width: "20vh", ml: 1 }}
                  variant="contained"
                  color="primary"
                >
                  現場リセット
                </Button>
              </Box>
            </Grid2>
          </>
        )}
      </Box>
    </>
  );
};

export default SettingVarious;